import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import LanIcon from '@mui/icons-material/Lan';
import CategoryIcon from '@mui/icons-material/Category';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Paper from '@mui/material/Paper';
import PeriodSearchForm from '../components/PeriodSearchForm';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';

import * as clinicHttp from '../http/clinics';
import * as examsHttp from '../http/exams'
import * as authHttp from '../http/auth';
import * as utils from '../utils';
import * as userutils from '../userutils';

const styles = theme => ({
  root: {
    width: '100%',
  },
  title: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    verticalAlign: 'baseline',
  },
});

const mapStateToProps = (state) => {
  return {
    clinics: state.clinics,
    exams: state.exams.list,
    users: state.clinics.users,
    categories: state.clinics.categories,
  };
};

function TimelineExamDoneStepIcon(props: StepIconProps) {
  return (
    <Tooltip disableFocusListener title="Horário do exame">
      <MedicalServicesIcon fontSize="small" />
    </Tooltip>
  );
}

function TimelineExamCreatedStepIcon(props: StepIconProps) {
  return (
    <Tooltip disableFocusListener title="Horário de importação ao PACS">
      <MedicalInformationIcon fontSize="small" />
    </Tooltip>
  );
}

function TimelineImportedRisStepIcon(props: StepIconProps) {
  return (
    <Tooltip disableFocusListener title="Horário de importação ao ImagiRIS">
      <LanIcon fontSize="small" />
    </Tooltip>
  );
}

function TimelineCategoryStepIcon(props: StepIconProps) {
  return (
    <Tooltip disableFocusListener title="Horário de categorização">
      <CategoryIcon fontSize="small" />
    </Tooltip>
  );
}

function TimelineLiberadoStepIcon(props: StepIconProps) {
  return (
    <Tooltip disableFocusListener title="Horário de liberação">
      <Check fontSize="small" />
    </Tooltip>
  );
}

function ExamsTimelineReport(props) {
  const { classes,
    clinics,
    exams,
    users,
    categories,
  } = props;

  const [studyClinic, setStudyClinic] = useState('');
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [order, setOrder] = useState("");

  const orders = [
    { label: "Data do exame", value: "study_datetime" },
    { label: "Nome do Paciente", value: "patient_name" }
  ];

  useEffect(() => {
    authHttp.getCurrentUser();
    clinicHttp.list();
  }, []);

  const handleClinicChange = useCallback(
    event => {
      setStudyClinic(event.target.value);
    },
    []
  )

  const doQuery = useCallback(() => {
    let formattedFromDate = utils.formatDateToString(fromDate);
    let formattedToDate = utils.formatDateToString(toDate);

    if (formattedFromDate === null)
      formattedFromDate = utils.formatDateToString(new Date());
    if (formattedToDate === null)
      formattedToDate = utils.formatDateToString(new Date());

    const filters = {
      clinic: studyClinic,
      studyDatetimeFrom: formattedFromDate,
      studyDatetimeTo: formattedToDate,
      status: "liberado",
      ordering: order
    };
    examsHttp.list(filters);
  }, [fromDate, toDate, studyClinic, order]);

  const setFromDateCallback = useCallback((date) => {
    setFromDate(date);
  }, []);

  const setToDateCallback = useCallback((date) => {
    setToDate(date);
  }, []);

  const getUserName = useCallback(
    userId => {
      return userutils.getUserName(userId, users);
    },
    [users]
  )

  //  const errorComponent = errorMessage != null ?
  //      <Typography variant="subtitle1" color="error">{errorMessage}</Typography>
  //      : null
  //
  //  const loadingComponent = loading ?
  //      <Typography variant="subtitle1" color="primary">Carregando</Typography>
  //      : null
  //      <FormControl className={classes.formControl}>
  //        { loadingComponent }
  //      </FormControl>
  //    { errorComponent }

  const steps = ['Exame', 'Incl no PACS', 'Incl ImagiRIS', 'Marc cat.', 'Liberação'];

  const populateStepLabelProps = useCallback(
    (step, index, stepProps, labelProps, exam) => {
      if (index === 0) {
        labelProps.optional = (
          <Typography variant="caption">+0</Typography>
        );
        labelProps.StepIconComponent = TimelineExamDoneStepIcon;
        return utils.formatDate(exam.study_datetime);
      } else if (index === 1) {
        labelProps.optional = (
          <Typography variant="caption">{utils.dateDiff(exam.study_datetime, exam.exam.created_time).humanize()}</Typography>
        );
        labelProps.StepIconComponent = TimelineExamCreatedStepIcon;
        return utils.formatDate(exam.exam.created_time);
      } else if (index === 2) {
        labelProps.optional = (
          <Typography variant="caption">{utils.dateDiff(exam.exam.created_time, exam.exam.imported_datetime).humanize()}</Typography>
        );
        labelProps.StepIconComponent = TimelineImportedRisStepIcon;
        return utils.formatDate(exam.exam.imported_datetime);
      } else if (index === 3) {
        labelProps.optional = (
          <Typography variant="caption">{utils.dateDiff(exam.exam.imported_datetime, exam.category_timestamp).humanize()}</Typography>
        );
        labelProps.StepIconComponent = TimelineCategoryStepIcon;
        return utils.formatDate(exam.category_timestamp);
      } else if (index === 4) {
        labelProps.optional = (
          <Typography variant="caption">{utils.dateDiff(exam.category_timestamp, exam.report?.finished_timestamp).humanize()}</Typography>
        );
        labelProps.StepIconComponent = TimelineLiberadoStepIcon;
        return utils.formatDate(exam.report?.finished_timestamp);
      }
    }, []
  );

  return <div className={classes.root}>
    <form>
      <PeriodSearchForm fromDate={fromDate} toDate={toDate} setFromDate={setFromDateCallback} setToDate={setToDateCallback} />
      <FormControl className={classes.formControl}>
        <InputLabel htmlFor="clinics-selector">Clínica</InputLabel>
        <Select
          value={studyClinic || ""}
          inputProps={{
            name: "clinics",
            id: "clinics-selector",
          }}
          label="Clínica"
          onChange={handleClinicChange}
        >
          <MenuItem>
            <em>Todas</em>
          </MenuItem>
          {clinics &&
            clinics.list.map((clinic) => (
              <MenuItem key={clinic.id} value={clinic.id}>
                {clinic.name}
              </MenuItem>
            ))
          }
        </Select>
      </FormControl>
      <FormControl className={classes.formControl} style={{ top: "6px" }}>
        <InputLabel htmlFor="order-selector">Ordenar por</InputLabel>
        <Select
          value={order}
          inputProps={{
            name: "order",
            id: "order-selector",
          }}
          label="Ordernar por"
          onChange={(order) => setOrder(order.target.value)}
        >
          <MenuItem value="">
            <em>...</em>
          </MenuItem>
          {orders.map((order) => (
            <MenuItem key={order.value} value={order.value}>
              {order.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button variant="contained" color="primary" onClick={doQuery}>
        Carregar
      </Button>
    </form>
    <Typography className={classes.title} style={{ paddingTop: "20px" }} variant="h4">Exames</Typography>
    <Grid container spacing={2}>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Exame</TableCell>
              <TableCell align="right">Mod</TableCell>
              <TableCell align="right">Categoria</TableCell>
              <TableCell align="right">Médico</TableCell>
              <TableCell>
                <Stepper alternativeLabel activeStep={5}>
                  {steps.map((label, index) => {
                    const stepProps: { completed?: true } = {};
                    const labelProps: {
                      optional?: React.ReactNode;
                    } = {};
                    if (index === 0) {
                      labelProps.StepIconComponent = MedicalServicesIcon;
                    } else if (index === 1) {
                      labelProps.StepIconComponent = MedicalInformationIcon;
                    } else if (index === 2) {
                      labelProps.StepIconComponent = LanIcon;
                    } else if (index === 3) {
                      labelProps.StepIconComponent = CategoryIcon;
                    } else if (index === 4) {
                      labelProps.StepIconComponent = Check;
                    }
                    return (
                      <Step key={label} {...stepProps}>
                        <StepLabel {...labelProps}>{label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exams.map((exam) => (
              <TableRow
                key={exam.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {exam.patient_name}
                </TableCell>
                <TableCell align="right">{exam.modality}</TableCell>
                <TableCell align="right">{categories[exam.category]?.name}</TableCell>
                <TableCell align="right">{getUserName(exam.report?.medic)}</TableCell>
                <TableCell align="center">
                  <Stepper alternativeLabel activeStep={5}>
                    {steps.map((label, index) => {
                      const stepProps: { completed?: true } = {};
                      const labelProps: {
                        optional?: React.ReactNode;
                      } = {};
                      const data = populateStepLabelProps(label, index, stepProps, labelProps, exam)
                      return (
                        <Step key={label} {...stepProps}>
                          <StepLabel {...labelProps}>{data}</StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  </div>
};

export default withStyles(styles)(connect(mapStateToProps)(ExamsTimelineReport));